import React from 'react';
import { ArrowRight, Sparkles, TrendingUp, AlertTriangle, DollarSign } from 'lucide-react';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_80a9eaca-abc5-4772-88de-8f55ddf92961/artifacts/nma1f5im_Screenshot%202026-07-14%20at%204.51.15%E2%80%AFPM.png';

/**
 * V2A — The Sales-Forward Powerhouse
 * Deep purple, product-forward, floating insight cards, high-energy.
 */
export default function LandingV2A({ onGoToLogin, onGoToSignup }) {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#0A051A', fontFamily: 'Inter, system-ui, sans-serif', color: '#fff' }}>
      {/* Ambient background orbs */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-[500px] w-[500px] rounded-full opacity-40" style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)', filter: 'blur(80px)' }} />
      <div className="pointer-events-none absolute top-1/3 -right-32 h-[600px] w-[600px] rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #F472B6 0%, transparent 70%)', filter: 'blur(100px)' }} />

      {/* Nav */}
      <nav className="relative z-20 max-w-7xl mx-auto px-6 pt-6 flex items-center justify-between" data-testid="v2a-nav">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg" style={{ background: 'linear-gradient(135deg, #7C3AED, #F472B6)' }} />
          <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', fontWeight: 800 }}>LodgiFlow</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
          <a href="#platform" className="hover:text-white transition-colors">Platform</a>
          <a href="#results" className="hover:text-white transition-colors">Results</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onGoToLogin} className="text-sm text-white/70 hover:text-white transition-colors" data-testid="v2a-login">Log In</button>
          <button onClick={onGoToSignup} className="px-5 py-2 rounded-xl text-sm font-semibold transition-transform hover:scale-105" style={{ background: 'linear-gradient(135deg, #7C3AED, #F472B6)' }} data-testid="v2a-cta-nav">Get Started →</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
        <div className="animate-[slideInLeft_0.7s_ease-out]">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-6 border border-purple-500/40" style={{ background: 'rgba(124,58,237,0.15)' }}>
            <Sparkles className="h-3.5 w-3.5 text-purple-300" />
            <span className="text-xs font-semibold text-purple-200 tracking-wide">HOSPITALITY TECH GOVERNANCE</span>
          </div>
          <h1 style={{ fontFamily: 'Outfit', fontWeight: 900, fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', lineHeight: 1.02, letterSpacing: '-0.03em' }} data-testid="v2a-headline">
            Your tech stack is a <span style={{ background: 'linear-gradient(135deg, #C084FC 0%, #F472B6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>mess.</span><br />
            We fix that.
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
            Get total visibility into your $20M+ hospitality tech portfolio. Stop wasting money on redundant software and start optimizing operations today.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={onGoToSignup} className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-bold transition-transform hover:scale-[1.03]" style={{ background: 'linear-gradient(135deg, #7C3AED, #F472B6)', boxShadow: '0 10px 40px -10px rgba(124,58,237,0.6)' }} data-testid="v2a-cta-primary">
              Get a Free Audit
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button onClick={onGoToLogin} className="px-6 py-3.5 rounded-xl text-sm font-semibold border border-white/20 hover:border-white/40 hover:bg-white/5 transition-colors" data-testid="v2a-cta-secondary">
              Explore Platform
            </button>
          </div>
          <div className="mt-8 flex items-center gap-6 text-xs text-white/50">
            <span>No credit card required</span>
            <span className="h-1 w-1 rounded-full bg-white/30" />
            <span>SOC 2 · GDPR ready</span>
            <span className="h-1 w-1 rounded-full bg-white/30" />
            <span>Setup in 24h</span>
          </div>
        </div>

        {/* Right — dashboard mockup with floating cards */}
        <div className="relative animate-[fadeInScale_0.9s_ease-out_0.15s_both]">
          <div className="relative rounded-2xl overflow-hidden border border-white/10" style={{ boxShadow: '0 40px 80px -20px rgba(124,58,237,0.4)' }}>
            <img src={HERO_IMG} alt="LodgiFlow Executive Overview" className="w-full h-auto block" />
          </div>
          {/* Floating insight cards */}
          <div className="absolute -top-4 -left-4 rounded-xl p-3 backdrop-blur-lg border border-white/20 hidden md:flex items-center gap-2.5 animate-[floatY_4s_ease-in-out_infinite]" style={{ background: 'rgba(255,255,255,0.12)' }}>
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(74,222,128,0.2)' }}>
              <DollarSign className="h-4 w-4 text-emerald-300" />
            </div>
            <div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Savings</div>
              <div className="text-sm font-bold">$620K found</div>
            </div>
          </div>
          <div className="absolute top-1/3 -right-4 rounded-xl p-3 backdrop-blur-lg border border-white/20 hidden md:flex items-center gap-2.5 animate-[floatY_5s_ease-in-out_infinite_1s]" style={{ background: 'rgba(255,255,255,0.12)' }}>
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(251,146,60,0.2)' }}>
              <AlertTriangle className="h-4 w-4 text-orange-300" />
            </div>
            <div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Renewal</div>
              <div className="text-sm font-bold">45 days</div>
            </div>
          </div>
          <div className="absolute -bottom-4 left-1/4 rounded-xl p-3 backdrop-blur-lg border border-white/20 hidden md:flex items-center gap-2.5 animate-[floatY_4.5s_ease-in-out_infinite_2s]" style={{ background: 'rgba(255,255,255,0.12)' }}>
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(244,114,182,0.2)' }}>
              <TrendingUp className="h-4 w-4 text-pink-300" />
            </div>
            <div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Anomaly</div>
              <div className="text-sm font-bold">3 flagged</div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics strip fold */}
      <section className="relative z-10 border-t border-white/10 backdrop-blur-sm" style={{ background: 'rgba(26,11,59,0.5)' }}>
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="v2a-metrics">
          {[
            { k: '15%', v: 'Average portfolio savings identified', c: '#C084FC' },
            { k: '48h', v: 'From onboarding to first insight', c: '#F472B6' },
            { k: '900+', v: 'Products & vendors catalogued', c: '#60A5FA' },
          ].map((m) => (
            <div key={m.k} className="text-center md:text-left">
              <div style={{ fontFamily: 'Outfit', fontWeight: 900, fontSize: '3.5rem', color: m.c, lineHeight: 1 }}>{m.k}</div>
              <div className="mt-2 text-sm text-white/60">{m.v}</div>
            </div>
          ))}
        </div>
      </section>

      <style>{`
        @keyframes slideInLeft { from { opacity:0; transform:translateX(-24px); } to { opacity:1; transform:translateX(0); } }
        @keyframes fadeInScale { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
        @keyframes floatY { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
      `}</style>
    </div>
  );
}
